import Loading from 'components/Loading';
import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PivotGrid from 'components/PivotGrid/PivotGrid';
import { checkBoxEnabledStyle, style } from 'components/PivotGrid/defaultStyle';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { usePrimaryColor } from 'styles/theme';
import { useGetSoftwares } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/softwaresIntegrations';
import { PivotFieldsAggridT } from 'business/definitions';
import { useQueryClient } from '@tanstack/react-query';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';
import { Box } from '@mui/material';
import { styled, SxProps } from '@mui/material/styles';
import { hideTCDColumnByYear } from 'ui/AgGridReact/helpers';
import {
    ColDef,
    FilterChangedEvent,
    FilterModel,
    SideBarDef,
} from 'ag-grid-community';
import {
    getImputationDimensionsCodes,
    useGetImputationDimensions,
} from 'routes/BudgetModule/config/hooks';
import { aggFuncs } from './gridHelpers';
import AmountWithInscriptionsButtonCellRenderer from './AmountWithInscriptionsButtonCellRenderer';
import { addEvolutionColumnValues } from './evolutionColHelpers';
import {
    getColDefsForColumnsPivotedByYear,
    getColDefs,
} from './colDefinitions';
import { AgGridReactProps } from 'ag-grid-react';
import {
    BUDGET_EXCEL_EXPORT_PARAMS,
    BUDGET_EXCEL_STYLES,
    getBudgetExcelStringCellClass,
} from 'routes/BudgetModule/config/excelExportHelpers';

const SIDE_BAR: SideBarDef = {
    toolPanels: [
        {
            id: 'columns',
            labelDefault: 'sidebar',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressPivotMode: true,
            },
        },
        {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            toolPanelParams: {
                suppressFilterSearch: true,
                suppressExpandAll: true,
                suppressSyncLayoutWithGrid: true,
            },
        },
    ],
};

type Props = {
    data: ResponseDataT[];
    // FIXME: this probably can be removed for colDefs only
    selectedYear: number;
    pivotFieldsAggrid: PivotFieldsAggridT | undefined;
    updateGridFilters?: (event: FilterChangedEvent) => void;
    gridFilters?: FilterModel;
    onPivotFieldsAggridUpdate: (arg1: PivotFieldsAggridT) => void;
    isLoading: boolean;
    setExporterToExcel: ((arg1: FlowAnyFunction) => void) | undefined;
    gridProps?: Partial<AgGridReactProps>;
    budgetProposal: BudgetProposalT;
    onInscriptionsModalOpened: (arg1: void) => void;
    onInscriptionsModalClosed: (arg1: void) => void;
    baseFilters: QueryFilterT[];
    pagination?: boolean;
    sx?: SxProps;
};

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    width: '100%',
    '& .mui-pivot-grid-wrapper': {
        ...style,
        ...checkBoxEnabledStyle,
    },
    '& .ag-theme-material': { width: '100%', fontFamily: 'Roboto' },
});

const BudgetPivotGrid = ({
    data,
    selectedYear,
    isLoading,
    pivotFieldsAggrid,
    updateGridFilters = undefined,
    gridFilters = undefined,
    onPivotFieldsAggridUpdate,
    gridProps = {},
    setExporterToExcel,
    budgetProposal,
    onInscriptionsModalOpened: onInscriptionsModelOpenedProp,
    onInscriptionsModalClosed: onInscriptionsModalClosedProp,
    baseFilters,
    pagination = false,
    sx = undefined,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { phase: budgetPhase } = budgetProposal;
    const softwaresIntegrations = useGetSoftwares();
    const imputationRawDimensions = useContext(ImputationDimensionsContext);
    const imputationDimensions = useGetImputationDimensions(true);
    const imputationDimensionsCodes = getImputationDimensionsCodes(
        imputationRawDimensions,
    );

    const onInscriptionsModalOpened = useCallback(
        onInscriptionsModelOpenedProp,
        [],
    );
    const onInscriptionsModalClosed = useCallback(() => {
        onInscriptionsModalClosedProp();
    }, [onInscriptionsModalClosedProp, queryClient]);

    const dataWithEvolution = useMemo(
        () =>
            addEvolutionColumnValues(
                budgetPhase,
                selectedYear,
                data,
                imputationDimensionsCodes,
            ),
        [budgetPhase, selectedYear, data],
    );

    // this is what allows us to display columns conditionally on the pivoted year (ex in exercice - 3 we only display realized amount)
    // see docs here: https://www.ag-grid.com/react-grid/pivoting/#manipulating-secondary-columns
    const processPivotResultColDef = useCallback(
        (colDef: ColDef) =>
            hideTCDColumnByYear(
                colDef,
                selectedYear,
                getColDefsForColumnsPivotedByYear,
                budgetPhase,
            ),
        [selectedYear, budgetPhase],
    );

    // we need to memoize initialColDef to avoid useless PivotGrid re renders
    // if we don't do this column state changes are not taken into account
    // FIXME: relying on memo to work is not correct - fix this.
    const primaryColor = usePrimaryColor();
    const initialColDefs = useMemo(
        () =>
            getColDefs(
                imputationDimensions,
                t,
                budgetProposal,
                primaryColor,
                onInscriptionsModalOpened,
                onInscriptionsModalClosed,
                baseFilters,
                softwaresIntegrations,
            ),
        [
            JSON.stringify(imputationDimensions),
            t,
            budgetProposal,
            primaryColor,
            onInscriptionsModalOpened,
            onInscriptionsModalClosed,
            JSON.stringify(baseFilters),
        ],
    );

    const enrichedGridProps: Partial<AgGridReactProps> = useMemo(
        () => ({
            aggFuncs,
            processPivotResultColDef,
            sideBar: SIDE_BAR,
            components: {
                amountWithInscriptionsButtonCellRenderer:
                    AmountWithInscriptionsButtonCellRenderer,
                ...gridProps.components,
            },
            pagination,
            paginationPageSize: 10,
            ...gridProps,
        }),
        [processPivotResultColDef, gridProps, pagination],
    );

    // using our loading in addition of Aggrid's one in PivotGrid
    // otherwise the loading was bugging when refreshing data via analyse filters
    if (isLoading) {
        return (
            <StyGrid>
                <Loading />
            </StyGrid>
        );
    }

    return (
        <StyGrid sx={sx}>
            <Box
                className="mui-pivot-grid-wrapper"
                sx={{ height: '100', width: '100%' }}
            >
                <PivotGrid
                    data={dataWithEvolution}
                    pivotFieldsAggrid={pivotFieldsAggrid}
                    updateGridFilters={updateGridFilters}
                    gridFilters={gridFilters}
                    initialColDefs={initialColDefs}
                    onColumnsChanged={onPivotFieldsAggridUpdate}
                    setExporterToExcel={setExporterToExcel}
                    gridProps={enrichedGridProps}
                    groupDisplayType="multipleColumns"
                    groupHideOpenParents={false}
                    defaultExcelExportParams={BUDGET_EXCEL_EXPORT_PARAMS}
                    excelStyles={BUDGET_EXCEL_STYLES}
                    getGroupRowCellClass={getBudgetExcelStringCellClass}
                />
            </Box>
        </StyGrid>
    );
};

export default memo(BudgetPivotGrid);
